import React, {useCallback, useEffect, useRef} from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import { Provider, useSelector } from 'react-redux';

import { map } from './Map';
import store from '../store';
import { useHistory } from 'react-router-dom';
import StatusView from './StatusView';
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import StartsStopsView from "./StartsStopsView";
import {ThemeProvider} from "@material-ui/core/styles";
import {useTheme} from "@material-ui/core";

const PositionsMap = ({ positions, replayBack }) => {
  const id = 'positions';
  const clusters = `${id}-clusters`;

  const theme = useTheme();
  const history = useHistory();

  const devices = useSelector(state => state.devices.items);
  const mapType = useSelector(state => state.session.user && state.session.user.map);

  const currentPos  = useRef(null);
  const activePopupRef = useRef(null);
  const activeDeviceIdRef = useRef(null);
  const checkOneTime = useRef(false);

  const [openSnackbar, setOpenSnackbar] = React.useState(true);

  currentPos.current = positions[0];

  const deviceColor = device => {
    switch (device.status) {
      case 'online':
        return 'green';
      case 'offline':
        return 'gray';
      case 'slowing':
        return 'yellow';
      default:
        return 'red';
    }
  };

  const createFeature = (devices, position) => {
    const device = devices[position.deviceId];
    return {
      deviceId: position.deviceId,
      name: device.name,
      category: device.category || 'default',
      color: deviceColor(device),
    }
  };

  const onMouseEnter = () => map.getCanvas().style.cursor = 'pointer';
  const onMouseLeave = () => map.getCanvas().style.cursor = '';

  const onMarkerClick = useCallback(event => {
    const feature = event.features[0];
    const deviceId = feature.properties.deviceId; // Get the device ID
    let coordinates = feature.geometry.coordinates.slice();

    while (Math.abs(event.lngLat.lng - coordinates[0]) > 180)
      coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;

    if (activePopupRef.current)
      activePopupRef.current.remove();

    const placeholder = document.createElement('div');
    ReactDOM.render(
        <Provider store={store}>
          {!replayBack ?
              <ThemeProvider theme={theme}>
                <StatusView deviceId={feature.properties.deviceId} onShowDetails={positionId => history.push(`/position/${positionId}`)} history={history}/>
              </ThemeProvider>
              :
              <ThemeProvider theme={theme}>
                <StartsStopsView position={currentPos.current} status={"positionFixTime"} deviceName={devices[currentPos.current.deviceId].name} mapType={mapType}/>
              </ThemeProvider>
          }
        </Provider>,
        placeholder
    );

    const popup = new mapboxgl.Popup({
      offset: 25,
      anchor: 'top'
    })
        .setDOMContent(placeholder)
        .setLngLat(coordinates)
        .addTo(map);
    activePopupRef.current = popup;
    activeDeviceIdRef.current = deviceId;

    popup.on('close', () => {
      ReactDOM.unmountComponentAtNode(placeholder);
      activePopupRef.current = null;
      activeDeviceIdRef.current = null;
    });
  }, [history, theme]);

  const onClusterClick = event => {
    const features = map.queryRenderedFeatures(event.point, {
      layers: [clusters],
    });
    const clusterId = features[0].properties.cluster_id;
    map.getSource(id).getClusterExpansionZoom(clusterId, (error, zoom) => {
      if (!error) map.easeTo({
        center: features[0].geometry.coordinates,
        zoom: zoom,
      });
    });
  };

  useEffect(() => {
        map.addSource(id, {
          'type': 'geojson',
          'data': {
            type: 'FeatureCollection',
            features: [],
          },
          'cluster': true,
          'clusterMaxZoom': 14,
          'clusterRadius': 50,
        });
        map.addLayer({
          'id': id,
          'type': 'symbol',
          'source': id,
          'filter': ['!', ['has', 'point_count']],
          'layout': {
            'icon-image': '{category}-{color}',
            'icon-allow-overlap': true,
            'text-field': '{name}',
            'text-allow-overlap': true,
            'text-anchor': 'bottom',
            'text-offset': [0, -2],
            'text-font': ['Roboto Regular'],
            'text-size': 12,
          },
          'paint': {
            'text-halo-color': 'white',
            'text-halo-width': 1,
          },
        });
        map.addLayer({
          'id': clusters,
          'type': 'symbol',
          'source': id,
          'filter': ['has', 'point_count'],
          'layout': {
            'icon-image': 'background',
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Roboto Regular'],
            'text-size': 14,
          },
        });


        map.on('mouseenter', id, onMouseEnter);
        map.on('mouseleave', id, onMouseLeave);
        map.on('mouseenter', clusters, onMouseEnter);
        map.on('mouseleave', clusters, onMouseLeave);
        map.on('click', id, onMarkerClick);
        map.on('click', clusters, onClusterClick);

        return () => {
          Array.from(map.getContainer().getElementsByClassName('mapboxgl-popup')).forEach(el => el.remove());

          activePopupRef.current = null;
          activeDeviceIdRef.current = null;

          map.off('mouseenter', id, onMouseEnter);
          map.off('mouseleave', id, onMouseLeave);
          map.off('mouseenter', clusters, onMouseEnter);
          map.off('mouseleave', clusters, onMouseLeave);
          map.off('click', id, onMarkerClick);
          map.off('click', clusters, onClusterClick);

          map.removeLayer(id);
          map.removeLayer(clusters);
          map.removeSource(id);
        };
      },
      [onMarkerClick, theme]);


  useEffect(() => {
    const updateMap = async () => {
      setOpenSnackbar(true);
      const source = map.getSource(id);
      if (source) {
        await new Promise(resolve => {
          source.setData({
            type: 'FeatureCollection',
            features: positions
                .filter(it => devices.hasOwnProperty(it.deviceId))
                .map(position => ({
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: [position.longitude, position.latitude],
                  },
                  properties: createFeature(devices, position),
                })),
          });
          requestAnimationFrame(() => resolve());
        });
        if (activePopupRef.current && activeDeviceIdRef.current) {
          const currentOpenPosition = positions.find(p => p.deviceId === activeDeviceIdRef.current);
          if (currentOpenPosition) {
            activePopupRef.current.setLngLat([currentOpenPosition.longitude, currentOpenPosition.latitude]);
          }
        }
      }
      const coordinates = positions.map(item => [item.longitude, item.latitude]);
      if (coordinates.length && !checkOneTime.current) {
        const bounds = coordinates.reduce((bounds, item) => bounds.extend(item), new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
        map.fitBounds(bounds, {padding: { top: 50, bottom: 250, left: 25, right: 25 },});
        checkOneTime.current = true;
      }
    };
    updateMap();
  }, [devices, positions, theme]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  function snackbarColor(){
    return "error";
  }
  function message(id){
    return 'The devices "'+ id +'" have a poor signals '
  }
  function gpsSgnTest() {
    let device = ""
    let  i = 0
    if (positions.length > 0 && devices.length > 0){
      positions.forEach(position => {
        if (position.attributes.gpsSgn < 3) {
          i++;
          if (i > 1)
            device = device + " and " + devices[position.deviceId].name
          else
            device = devices[position.deviceId].name
        }
      })
       return  device;
    }else
      return "";
  }

  useEffect(() => {
    const styleId = 'mapbox-cyber-popup';
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    const isDarkMode = theme.palette.typeBackground === 'dark';

    const popupBg = isDarkMode ? 'rgba(15, 25, 35, 0.7)' : 'rgba(255, 255, 255, 0.5)';

    const glowOpacity = isDarkMode ? '0.4' : '0.2';

    styleElement.innerHTML = `
      .mapboxgl-popup-content {
        background: ${popupBg} !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
        border: 1px solid #00f2ff !important;
        box-shadow: 0 0 20px rgba(0, 242, 255, ${glowOpacity}) !important;
        border-radius: 20px !important;
        padding: 0 !important;
      }
      .mapboxgl-popup-tip {
        border-top-color: #00f2ff !important;
        border-bottom-color: #00f2ff !important;
        opacity: 0.6;
      }
      .mapboxgl-popup-close-button {
        color: #00f2ff !important;
        font-size: 16px !important;
        padding: 5px 8px !important;
        outline: none !important;
      }
      .mapboxgl-popup-close-button:hover {
        background-color: rgba(0, 242, 255, 0.1) !important;
        border-radius: 0 12px 0 0;
      }
    `;

    return () => {
      // If you want the style to disappear when the component unmounts:
      // if (styleElement) styleElement.remove();
    };
  }, [theme]);

  return (
      <>
        {openSnackbar && gpsSgnTest() !== "" ?
            <Snackbar open={openSnackbar} autoHideDuration={60000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} >
              <Alert onClose={handleCloseSnackbar } variant="filled" severity={snackbarColor()}>
                {message(gpsSgnTest())}
              </Alert>
            </Snackbar>
            :null}
        </>
  );
}

export default PositionsMap;
